import React, { useState } from 'react';
import { getLocalISOString } from '../utils/dateUtils';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import MultiplePaymentEvidenceUpload from './MultiplePaymentEvidenceUpload';
import { internalOrderService } from '../services/api';
import CategorySelector from './CategorySelector';
import OrderNotesTimeline from './OrderNotesTimeline';
import axios from 'axios';

// Normaliza método de pago a 'credito' cuando corresponda
const normalizePaymentMethod = (pm) => {
  if (!pm) return '';
  const v = pm.toLowerCase();
  if (v.includes('efect')) return 'efectivo';
  if (v.includes('transf') || v.includes('banc') || v.includes('consigna')) return 'transferencia';
  if (v.includes('credito') || v.includes('cartera') || v.includes('cliente_credito')) return 'credito';
  if (v.includes('pago_electronico') || v.includes('bold') || v.includes('mercadopago')) return 'pago_electronico';
  if (v.includes('contraentrega')) return 'contraentrega';
  if (v.includes('publicidad')) return 'publicidad';
  if (v.includes('reposicion')) return 'reposicion';
  return v;
};

// Componente CustomDropdown para reemplazar select nativo
const CustomDropdown = ({ value, onChange, options, placeholder, required }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
        style={{ zIndex: 1 }}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Icons.ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className="w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

const OrderReviewModal = ({ isOpen, onClose, order, onConfirm }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    delivery_method: '',
    payment_method: '',
    electronic_payment_type: '', // Tipo específico de pago electrónico
    electronic_payment_notes: '', // Notas para "otro" medio electrónico
    shipping_payment_method: 'contado', // contado o contraentrega
    shipping_date: '', // Fecha de envío para logística
    notes: '',
    is_service: false, // Nuevo flag para pedidos de solo servicio
    piso_receipt_number: '' // Consecutivo de gestión en piso (obligatorio para bodega)
  });

  // Estado para conteo de evidencias subidas
  const [evidenceCount, setEvidenceCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [internalOrders, setInternalOrders] = useState([]);
  const [internalOrdersLoading, setInternalOrdersLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Estado para validación de inventario
  const [inventoryCheck, setInventoryCheck] = useState({
    checking: false,
    valid: true,
    issues: []
  });

  // Estado para validación de mezclas A/B
  const [mixturesCheck, setMixturesCheck] = useState({
    checking: false,
    valid: true,
    affected_items: [],
    warnings: []
  });
  const [showMixturesModal, setShowMixturesModal] = useState(false);

  // Actualizar formData cuando cambie el pedido
  React.useEffect(() => {
    if (order && isOpen) {
      setEvidenceCount(0); // Reset evidence title
      // Calcular fecha de envío por defecto (hoy - mismo día)
      const defaultShippingDate = getLocalISOString().slice(0, 10);

      setFormData({
        delivery_method: order.delivery_method || '',
        payment_method: order.payment_method || '',
        electronic_payment_type: order.electronic_payment_type || '',
        electronic_payment_notes: order.electronic_payment_notes || '',
        shipping_payment_method: order.shipping_payment_method || 'contado',
        shipping_date: order.shipping_date || defaultShippingDate,
        notes: order.notes || '',
        is_service: order.is_service || false,
        piso_receipt_number: order.piso_receipt_number || ''
      });

      // Fetch existing categories for this order
      const fetchOrderCategories = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/categories/orders/${order.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            setSelectedCategories(response.data.data.map(c => c.id));
          }
        } catch (error) {
          console.error('Error fetching order categories:', error);
          setSelectedCategories([]);
        }
      };
      fetchOrderCategories();

      // Fetch internal orders
      const fetchInternalOrders = async () => {
        setInternalOrdersLoading(true);
        try {
          const response = await internalOrderService.getByParent(order.id);
          if (response && response.success) {
            setInternalOrders(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching internal orders:', error);
        } finally {
          setInternalOrdersLoading(false);
        }
      };

      fetchInternalOrders();

      // Validar inventario automáticamente
      validateInventory();

      // Validar mezclas automáticamente
      validateMixtures();
    } else {
      setInternalOrders([]);
      setInventoryCheck({ checking: false, valid: true, issues: [] });
      setMixturesCheck({ checking: false, valid: true, affected_items: [], warnings: [] });
    }
  }, [order, isOpen]);

  // Función de validación de inventario
  const validateInventory = async () => {
    if (!order?.items || order.items.length === 0) {
      setInventoryCheck({ checking: false, valid: true, issues: [] });
      return;
    }

    try {
      setInventoryCheck(prev => ({ ...prev, checking: true }));

      // Extraer items del pedido
      const items = order.items.map(item => ({
        product_code: item.product_code || item.code,
        quantity: item.quantity
      }));

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/inventory/validate-order`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setInventoryCheck({
          checking: false,
          valid: response.data.valid,
          issues: response.data.issues || []
        });

        if (!response.data.valid) {
          console.log('🔴 Inventario insuficiente:', response.data.issues);
          toast.error(`⚠️ ${response.data.issues.length} producto(s) sin inventario suficiente`);
        } else {
          console.log('✅ Inventario válido');
        }
      }
    } catch (error) {
      console.error('Error validando inventario:', error);
      // En caso de error, permitir continuar (no bloquear por error de validación)
      setInventoryCheck({ checking: false, valid: true, issues: [] });
    }
  };

  // Función de validación de mezclas A/B
  const validateMixtures = async () => {
    if (!order?.id) {
      setMixturesCheck({ checking: false, valid: true, affected_items: [], warnings: [] });
      return;
    }

    try {
      console.log('🔍 [OrderReview] Validating mixtures for order:', order.id);
      setMixturesCheck(prev => ({ ...prev, checking: true }));

      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/validate-mixtures/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('📥 [OrderReview] Mixtures validation response:', response.data);

      if (response.data.success) {
        const canProceed = response.data.can_proceed !== false;
        const affectedItems = response.data.affected_items || [];

        setMixturesCheck({
          checking: false,
          valid: canProceed,
          affected_items: affectedItems,
          warnings: response.data.warnings || []
        });

        if (!canProceed) {
          console.error('🚫 [OrderReview] MIXTURES BLOCKING:', affectedItems);
          // Mostrar modal en lugar de solo toast
          setShowMixturesModal(true);
          toast.error(`⚠️ Desbalance de mezclas: ${affectedItems.length} producto(s) afectados`, {
            duration: 6000
          });
        } else {
          console.log('✅ [OrderReview] Mixtures distribution valid');
        }
      }
    } catch (error) {
      console.error('Error validando mezclas:', error);
      // En caso de error, permitir continuar (no bloquear por error de validación)
      setMixturesCheck({ checking: false, valid: true, affected_items: [], warnings: [] });
    }
  };

  const [deliveryMethods, setDeliveryMethods] = useState([]);

  // Cargar métodos de envío dinámicamente desde la API
  React.useEffect(() => {
    const fetchDeliveryMethods = async () => {
      try {
        const response = await fetch('/api/delivery-methods/active', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const apiResponse = await response.json();
          // Manejar diferentes estructuras de respuesta de la API
          const methodsData = apiResponse.data || apiResponse;

          if (Array.isArray(methodsData)) {
            const dynamicMethods = methodsData.map(method => ({
              value: method.code,
              label: method.name
            }));
            setDeliveryMethods(dynamicMethods);
          } else {
            console.error('API response is not an array:', methodsData);
            throw new Error('Invalid API response format');
          }
        } else {
          console.error('Error cargando métodos de envío:', response.statusText);
          throw new Error('API request failed');
        }
      } catch (error) {
        console.error('Error cargando métodos de envío:', error);
        // Fallback actualizado con datos reales de la BD
        setDeliveryMethods([
          { value: 'recoge_bodega', label: 'Recoge Bodega - Cliente Físico' },
          { value: 'recoge_posterior', label: 'Cliente WhatsApp Recoge Bodega' },
          { value: 'domicilio', label: 'Domicilio' },
          { value: 'envio_nacional', label: 'Nacional' },
          { value: 'mensajeria_urbana', label: 'Mensajería Urbana' },
          { value: 'envio_especial', label: 'Envío Especial' }
        ]);
      }
    };

    if (isOpen) {
      fetchDeliveryMethods();
    }
  }, [isOpen]);

  const paymentMethods = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'cliente_credito', label: 'Cliente a Crédito' },
    { value: 'pago_electronico', label: 'Pago Electrónico' },
    { value: 'contraentrega', label: 'Contraentrega (Solo Bogotá)' },
    { value: 'publicidad', label: 'Publicidad (sin validación)' },
    { value: 'reposicion', label: 'Reposición (sin validación)' }
  ];

  // Helper para detectar "Recoge en Bodega"
  const isPickupDelivery = (code) => {
    if (!code) return false;
    const c = String(code).toLowerCase();
    // Acepta códigos comunes y coincidencias por texto
    return ['recogida_tienda', 'recoge_bodega', 'recoge_posterior'].includes(c) || c.includes('bodega') || c.includes('recoge');
  };

  // Nuevo helper: detectar entregas locales/domicilio/mensajería
  const isLocalDelivery = (code) => {
    if (!code) return false;
    const c = String(code).toLowerCase();
    const codes = ['domicilio', 'domicilio_local', 'domicilio_ciudad', 'mensajeria_urbana', 'mensajeria_local'];
    return codes.includes(c) || c.includes('domicilio') || c.includes('mensajeria') || c.includes('mensajería');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updates = { ...prev, [field]: value };

      // Si se marca como servicio, limpiar campos de envío
      if (field === 'is_service' && value === true) {
        updates.delivery_method = '';
        updates.shipping_date = '';
      }
      // Si se desmarca servicio, restaurar fecha por defecto si está vacía
      if (field === 'is_service' && value === false && !updates.shipping_date) {
        updates.shipping_date = getLocalISOString().slice(0, 10);
      }

      return updates;
    });
  };

  //  Helper function to save categories
  const saveCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/categories/orders/${order.id}`,
        { categoryIds: selectedCategories },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving categories:', error);
      throw error;
    }
  };

  const handleSendToWallet = async () => {
    // Validar categorías (obligatorio)
    if (selectedCategories.length === 0) {
      toast.error('Debe seleccionar al menos una categoría de cliente');
      return;
    }

    // Validar método de pago y fecha de envío
    if (!formData.payment_method) {
      toast.error('Debe seleccionar un método de pago');
      return;
    }

    // Si es servicio, enviar directo a cartera sin validar envío
    if (formData.is_service) {
      setLoading(true);
      try {
        const dataToSend = {
          orderId: order.id,
          payment_method: normalizePaymentMethod(formData.payment_method),
          electronic_payment_type: formData.electronic_payment_type,
          electronic_payment_notes: formData.electronic_payment_notes,
          shipping_date: null, // No aplica
          delivery_method: null, // No aplica
          notes: formData.notes,
          action: 'send_to_wallet',
          is_service: true,
          piso_receipt_number: formData.piso_receipt_number || null
        };

        await onConfirm(dataToSend);

        // Save categories
        await saveCategories();
        onClose();
        toast.success('Pedido de servicio enviado a cartera para validación');
      } catch (error) {
        console.error('Error enviando servicio a cartera:', error);
        toast.error('Error enviando pedido a cartera');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Si es efectivo + recoge en bodega => enviar a CARTERA (reciben el pago físicamente)
    if (formData.payment_method === 'efectivo' && isPickupDelivery(formData.delivery_method)) {
      if (!formData.delivery_method) {
        toast.error('Para Efectivo + Recoge en Bodega debe seleccionar el método de envío');
        return;
      }
      if (!formData.shipping_date) {
        toast.error('Debe seleccionar una fecha de envío');
        return;
      }

      setLoading(true);
      try {
        const dataToSend = {
          orderId: order.id,
          payment_method: formData.payment_method,
          delivery_method: formData.delivery_method,
          shipping_date: formData.shipping_date,
          notes: formData.notes,
          action: 'send_to_wallet',
          piso_receipt_number: formData.piso_receipt_number || null
        };

        await onConfirm(dataToSend);
        onClose();
        toast.success('Pedido enviado a Cartera. Cartera recibirá el pago en efectivo al entregar el pedido.');
      } catch (error) {
        console.error('Error enviando efectivo+bodega a cartera:', error);
        toast.error('Error enviando pedido a cartera');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Si es efectivo + domicilio/mensajería local => enviar a Logística (mensajero recibe el pago)
    if (formData.payment_method === 'efectivo' && isLocalDelivery(formData.delivery_method)) {
      if (!formData.delivery_method) {
        toast.error('Para Efectivo debe seleccionar un método de envío');
        return;
      }
      if (!formData.shipping_date) {
        toast.error('Debe seleccionar una fecha de envío');
        return;
      }

      setLoading(true);
      try {
        const dataToSend = {
          orderId: order.id,
          payment_method: formData.payment_method,
          delivery_method: formData.delivery_method,
          shipping_date: formData.shipping_date,
          notes: formData.notes,
          action: 'send_to_logistics',
          piso_receipt_number: formData.piso_receipt_number || null
        };

        await onConfirm(dataToSend);
        onClose();
        toast.success('Pedido en efectivo enviado a Logística. Se recibe el dinero al momento de la entrega y se cuadra con Cartera.');
      } catch (error) {
        console.error('Error enviando efectivo a logística:', error);
        toast.error('Error enviando pedido a logística');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Si es Publicidad o Reposición, pasa directo a Logística (sin validación de Cartera)
    if (['publicidad', 'reposicion'].includes(formData.payment_method)) {
      if (!formData.delivery_method) {
        toast.error('Para Publicidad/Reposición debe seleccionar un método de envío para enviarlo a Logística');
        return;
      }
      if (!formData.shipping_date) {
        toast.error('Debe seleccionar una fecha de envío');
        return;
      }

      setLoading(true);
      try {
        const dataToSend = {
          orderId: order.id,
          payment_method: formData.payment_method,
          delivery_method: formData.delivery_method,
          shipping_date: formData.shipping_date,
          notes: formData.notes,
          action: 'send_to_logistics',
          piso_receipt_number: formData.piso_receipt_number || null
        };

        await onConfirm(dataToSend);
        onClose();
        toast.success('Pedido enviado a Logística (Publicidad/Reposición).');
      } catch (error) {
        console.error('Error enviando publicidad/reposición a logística:', error);
        toast.error('Error enviando pedido a logística');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Si es contraentrega, pasa directo a Logística (no a Cartera)
    if (formData.payment_method === 'contraentrega') {
      if (!formData.delivery_method) {
        toast.error('Para Contraentrega debe seleccionar un método de envío para enviarlo a Logística');
        return;
      }
      if (!formData.shipping_date) {
        toast.error('Debe seleccionar una fecha de envío');
        return;
      }

      setLoading(true);
      try {
        const dataToSend = {
          orderId: order.id,
          payment_method: formData.payment_method,
          delivery_method: formData.delivery_method,
          shipping_date: formData.shipping_date,
          notes: formData.notes,
          action: 'send_to_logistics',
          piso_receipt_number: formData.piso_receipt_number || null
        };

        const devNorm = String(formData.delivery_method || '').toLowerCase();
        const pmLabel = pmNorm === 'contraentrega' ? 'Contraentrega' : (pmNorm === 'publicidad' ? 'Publicidad' : 'Reposición');
        const destination = (pmNorm === 'contraentrega' && devNorm !== 'nacional') ? 'Empaque' : 'Logística';

        await onConfirm(dataToSend);
        onClose();
        toast.success(`Pedido ${pmLabel} enviado a ${destination} exitosamente.`);
      } catch (error) {
        console.error('Error enviando contraentrega a logística:', error);
        toast.error('Error enviando pedido a logística');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validaciones específicas para pago electrónico
    if (formData.payment_method === 'pago_electronico') {
      if (!formData.electronic_payment_type) {
        toast.error('Debe seleccionar el tipo de pago electrónico');
        return;
      }
      if (formData.electronic_payment_type === 'otro' && !formData.electronic_payment_notes.trim()) {
        toast.error('Debe especificar qué otro medio electrónico se utilizó');
        return;
      }
    }

    // Validaciones específicas para pago electrónico
    if (formData.payment_method === 'pago_electronico') {
      // ... (existing checks)
    }

    if (!formData.shipping_date) {
      toast.error('Debe seleccionar una fecha de envío');
      return;
    }

    // VALIDACIÓN ESTRICTA (Security Check): Transferencia requiere comprobante si NO es servicio y NO es recogida
    // Aunque handleSendToWallet suele ser para otros casos, si cae aquí por Transferencia, validamos.
    const pmNorm = normalizePaymentMethod(formData.payment_method);
    if (pmNorm === 'transferencia') {
      const devNorm = String(formData.delivery_method || '').toLowerCase();
      const isPickup = isPickupDelivery(devNorm);

      if (!isPickup && !formData.is_service && evidenceCount === 0) {
        toast.error('❌ REQUERIDO: Para envíos nacionales o domicilios con transferencia, DEBE subir el comprobante de pago antes de continuar.');
        return;
      }
    }

    setLoading(true);
    try {
      const dataToSend = {
        orderId: order.id,
        payment_method: pmNorm,
        // ... (rest of data)
        electronic_payment_type: formData.electronic_payment_type,
        electronic_payment_notes: formData.electronic_payment_notes,
        shipping_date: formData.shipping_date,
        notes: formData.notes,
        action: 'send_to_wallet',
        piso_receipt_number: formData.piso_receipt_number || null
      };

      await onConfirm(dataToSend);
      onClose();
      toast.success('Pedido enviado a cartera para validación');
    } catch (error) {
      console.error('Error enviando a cartera:', error);
      toast.error('Error enviando pedido a cartera');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToLogistics = async () => {
    // Validar categorías (obligatorio)
    if (selectedCategories.length === 0) {
      toast.error('Debe seleccionar al menos una categoría de cliente');
      return;
    }

    // Validar método de pago, envío y fecha
    if (!formData.payment_method) {
      toast.error('Debe seleccionar un método de pago');
      return;
    }
    if (!formData.delivery_method) {
      toast.error('Debe seleccionar un método de envío');
      return;
    }
    if (!formData.shipping_date) {
      toast.error('Debe seleccionar una fecha de envío');
      return;
    }

    if (formData.is_service) {
      toast.error('Los pedidos de servicio no se envían a logística, deben ir a Cartera.');
      return;
    }

    // Reglas de negocio
    const pmNorm = normalizePaymentMethod(formData.payment_method);
    if (pmNorm === 'transferencia' || pmNorm === 'pago_electronico' || pmNorm === 'credito') {

      // VALIDACIÓN ESTRICTA: Transferencia requiere comprobante si es Envío Nacional/Domicilio
      if (pmNorm === 'transferencia') {
        // Asumimos que si está aquí es porque NO es servicio (ya validado arriba)
        // Validar si es envío físico (no recogida en tienda)
        const devNorm = String(formData.delivery_method || '').toLowerCase();
        const isPickup = isPickupDelivery(devNorm);

        if (!isPickup && evidenceCount === 0) {
          toast.error('❌ REQUERIDO: Para envíos nacionales o domicilios con transferencia, DEBE subir el comprobante de pago antes de continuar.');
          return;
        }
      }

      toast.error(pmNorm === 'credito'
        ? 'Los pedidos a crédito deben ir a Cartera para validación del cupo.'
        : 'Los pagos por transferencia/electrónicos deben ir a Cartera primero para verificar el abono.'
      );
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        orderId: order.id,
        payment_method: normalizePaymentMethod(formData.payment_method),
        delivery_method: formData.delivery_method,
        shipping_date: formData.shipping_date,
        notes: formData.notes,
        action: 'send_to_logistics',
        piso_receipt_number: formData.piso_receipt_number || null
      };

      await onConfirm(dataToSend);
      onClose();
      toast.success('Pedido enviado a Logística. Logística recibirá el dinero y luego lo cuadrará con Cartera.');
    } catch (error) {
      console.error('Error enviando a logística:', error);
      toast.error('Error enviando pedido a logística');
    } finally {
      setLoading(false);
    }
  };

  // Nueva función para procesamiento automático (solo para admin y facturador)
  const handleProcessOrder = async () => {
    // Validar categorías (obligatorio)
    if (selectedCategories.length === 0) {
      toast.error('Debe seleccionar al menos una categoría de cliente');
      return;
    }

    // Validaciones básicas
    if (!formData.payment_method) {
      toast.error('Debe seleccionar un método de pago');
      return;
    }

    // Validación para pedidos normales (no servicio)
    if (!formData.is_service) {
      if (!formData.delivery_method) {
        toast.error('Debe seleccionar un método de envío');
        return;
      }
      if (!formData.shipping_date) {
        toast.error('Debe seleccionar una fecha de envío');
        return;
      }
    }

    // Validaciones específicas para pago electrónico
    if (formData.payment_method === 'pago_electronico') {
      if (!formData.electronic_payment_type) {
        toast.error('Debe seleccionar el tipo de pago electrónico');
        return;
      }
      if (formData.electronic_payment_type === 'otro' && !formData.electronic_payment_notes.trim()) {
        toast.error('Debe especificar qué otro medio electrónico se utilizó');
        return;
      }
    }

    setLoading(true);
    try {
      // REGLAS DE NEGOCIO AUTOMÁTICAS
      const pmNorm = normalizePaymentMethod(formData.payment_method);
      const devNorm = String(formData.delivery_method || '').toLowerCase();
      const isPickup = isPickupDelivery(devNorm);
      const isLocal = isLocalDelivery(devNorm);

      console.log('🔄 Procesando pedido:', {
        orderId: order.id,
        payment: pmNorm,
        delivery: devNorm,
        isPickup,
        isLocal,
        evidenceCount,
        isService: formData.is_service
      });

      let actionType = '';
      let successMessage = '';

      if (pmNorm === 'contraentrega' || pmNorm === 'publicidad' || pmNorm === 'reposicion') {
        // Contraentrega y Publicidad/Reposición => directo a Logística
        actionType = 'send_to_logistics';
        successMessage = pmNorm === 'contraentrega'
          ? 'Pedido contraentrega enviado a Logística. Se recibe el dinero al momento de la entrega y se cuadra con Cartera.'
          : 'Pedido (Publicidad/Reposición) enviado a Logística.';
      } else if (formData.is_service) {
        // Pedido de servicio => directo a Cartera
        actionType = 'send_to_wallet';
        successMessage = 'Pedido de servicio enviado a Cartera para validación.';
      } else if (pmNorm === 'efectivo' && isPickup) {
        if (devNorm === 'recoge_posterior') {
          // WhatsApp Pickup => Logística (Empaque) primero, se paga al recoger
          actionType = 'send_to_logistics';
          successMessage = 'Pedido de WhatsApp (Recoge Bodega) enviado a Logística para empaque. Se registrará el pago al momento de la entrega física.';
          console.log('📦 Ruta: Efectivo + WhatsApp Pickup -> a Logística');
        } else {
          // Efectivo + Recoge en Bodega (POS) => CARTERA recibe el pago PRIMERO
          actionType = 'send_to_wallet';
          successMessage = 'Pedido enviado a Cartera para recibir el pago en efectivo. Después pasará a Logística.';
          console.log('💰 Ruta: Efectivo + Pickup Inmediato -> a Cartera');
        }
      } else if (pmNorm === 'efectivo' && isLocal) {
        // Efectivo + Domicilio/Mensajería local => Logística (mensajero cobra y cuadra con Cartera)
        actionType = 'send_to_logistics';
        successMessage = 'Pedido con pago en efectivo enviado a Logística. Se recibe el dinero al momento de la entrega y se cuadra con Cartera.';
        console.log('🏍️ Ruta: Efectivo + Local -> a Logística');
      } else if (pmNorm === 'transferencia' || pmNorm === 'pago_electronico' || pmNorm === 'credito') {

        // VALIDACIÓN ESTRICTA: Transferencia requiere comprobante si es Envío Nacional/Domicilio
        if (pmNorm === 'transferencia') {
          if (!isPickup && !formData.is_service && evidenceCount === 0) {
            toast.error('❌ REQUERIDO: Para envíos nacionales o domicilios con transferencia, DEBE subir el comprobante de pago antes de continuar.');
            return;
          }
        }

        // Transferencia, electrónicos o crédito => Cartera valida primero
        actionType = 'send_to_wallet';
        successMessage = pmNorm === 'credito'
          ? 'Pedido enviado a Cartera para validación de cupo de crédito.'
          : `Pedido procesado y enviado a Cartera para validación (${getPaymentMethodLabel(pmNorm)})`;
      } else {
        // Otros casos => Cartera
        actionType = 'send_to_wallet';
        successMessage = 'Pedido enviado a Cartera para validación.';
      }

      const dataToSend = {
        orderId: order.id,
        payment_method: pmNorm,
        delivery_method: formData.is_service ? null : formData.delivery_method,
        electronic_payment_type: formData.electronic_payment_type,
        electronic_payment_notes: formData.electronic_payment_notes,
        shipping_date: formData.is_service ? null : formData.shipping_date,
        notes: formData.notes,
        action: actionType,
        auto_processed: true,
        is_service: formData.is_service,
        piso_receipt_number: formData.piso_receipt_number || null
      };

      await onConfirm(dataToSend);
      await saveCategories(); // Save categories
      onClose();
      toast.success(successMessage);
    } catch (error) {
      console.error('Error procesando pedido:', error);
      toast.error('Error procesando el pedido');
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para obtener la etiqueta del método de pago
  const getPaymentMethodLabel = (method) => {
    const labels = {
      'efectivo': 'Efectivo',
      'transferencia': 'Transferencia',
      'cliente_credito': 'Cliente a Crédito',
      'credito': 'Cliente a Crédito',
      'pago_electronico': 'Pago Electrónico',
      'contraentrega': 'Contraentrega',
      'publicidad': 'Publicidad',
      'reposicion': 'Reposición'
    };
    return labels[method] || method;
  };

  const getRecommendedAction = () => {
    if (!formData.payment_method) {
      return null;
    }

    const pmNorm = normalizePaymentMethod(formData.payment_method);
    const devNorm = String(formData.delivery_method || '').toLowerCase();
    const isPickup = isPickupDelivery(devNorm);
    const isLocal = isLocalDelivery(devNorm);

    // Recomendación basada en reglas de negocio actualizadas (Feb 10, 2026)
    if (pmNorm === 'contraentrega') {
      if (devNorm === 'nacional') {
        return {
          action: 'logistics',
          reason: 'Pago contraentrega + Nacional: pasa a Logística para generación de guía.'
        };
      } else {
        return {
          action: 'logistics',
          reason: 'Pago contraentrega + Local: pasa directo a Empaque para despacho rápido.'
        };
      }
    } else if (['publicidad', 'reposicion'].includes(pmNorm)) {
      return {
        action: 'logistics',
        reason: 'Pedido de Publicidad/Reposición: pasa directo a Logística, no requiere validación de Cartera.'
      };
    } else if (formData.is_service) {
      return {
        action: 'wallet',
        reason: 'Pedido de servicio: pasa a Cartera para validación (sin logística).'
      };
    } else if (pmNorm === 'efectivo' && isPickup) {
      return {
        action: 'wallet',
        reason: 'Pago en efectivo + Recoge en Bodega: enviar a Cartera primero para recibir el dinero físicamente.'
      };
    } else if (pmNorm === 'efectivo' && isLocal) {
      return {
        action: 'logistics',
        reason: 'Pago en efectivo + domicilio/mensajería local: enviar a Logística. Se recibe el dinero al momento de la entrega y se cuadra con Cartera.'
      };
    } else if (pmNorm === 'transferencia') {
      return {
        action: 'wallet',
        reason: 'Transferencia: DEBE subir comprobantes y enviar a Cartera para validación.'
      };
    } else if (['pago_electronico', 'credito'].includes(pmNorm)) {
      return {
        action: 'wallet',
        reason: `${getPaymentMethodLabel(pmNorm)}: DEBE ir a Cartera primero para validación.`
      };
    } else {
      return {
        action: 'wallet',
        reason: 'Requiere validación de Cartera antes del envío'
      };
    }
  };

  const recommendation = getRecommendedAction();

  // Evitar duplicado de observaciones/notas cuando traen el mismo contenido
  const normalizeSection = (txt) => {
    if (!txt) return '';
    return String(txt)
      .replace(/(OBSERVACIONES SIIGO:|NOTAS SIIGO:|OBSERVACIONES:|NOTAS:)/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  };
  const normalizedObs = normalizeSection(order?.siigo_observations);
  const normalizedNotes = normalizeSection(order?.notes);
  const notesAreDuplicate = normalizedObs && normalizedNotes && (
    normalizedObs === normalizedNotes ||
    normalizedObs.includes(normalizedNotes) ||
    normalizedNotes.includes(normalizedObs)
  );

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-2xl max-w-7xl w-full max-h-[95vh] flex flex-col overflow-hidden relative" style={{ zIndex: 10000 }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Revisar Pedido - {order.order_number}
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              Configure los detalles del pedido antes de procesarlo
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icons.X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50">
          <div className="p-6">
            {/* Alerta de motivo de devolución (si existe) */}
            {order.validation_notes && (
              <div className="mb-6 bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Icons.AlertTriangle className="h-5 w-5 text-orange-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-xs font-medium text-orange-800">
                      Pedido devuelto por Cartera
                    </h3>
                    <div className="mt-2 text-xs text-orange-700">
                      <p className="font-bold">Motivo: {order.validation_notes}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Column 1: Order Details & Information (Left) */}
              <div className="space-y-2">
                {/* Información del pedido */}
                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                  <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-gray-100">
                    <Icons.User className="w-4 h-4 text-blue-500" />
                    <h3 className="font-semibold text-gray-900">Información del Cliente</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Cliente</span>
                      <span className="font-medium text-gray-900">{order.customer_name}</span>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Teléfono</span>
                      <span className="font-medium text-gray-900">{order.customer_phone}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Dirección</span>
                      <span className="font-medium text-gray-900">{order.customer_address}</span>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Ciudad</span>
                      <span className="font-medium text-gray-900">{order.customer_city || 'No especificada'}</span>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Departamento</span>
                      <span className="font-medium text-gray-900">{order.customer_department || 'No especificado'}</span>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Total Pedido</span>
                      <span className="font-bold text-lg text-green-600">
                        ${Number(order.total_amount || 0).toLocaleString('es-CO')}
                      </span>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <span className="text-gray-500 block text-xs uppercase font-semibold">Cant. Productos</span>
                      <span className="font-medium text-gray-900">{order.items?.length || 0} items</span>
                    </div>
                  </div>
                </div>

                {/* Categorización del Cliente */}
                <CategorySelector
                  selectedCategories={selectedCategories}
                  onChange={setSelectedCategories}
                  required={true}
                />



                {/* Lista compacta de items del pedido */}
                {((order.items && order.items.length > 0) || (internalOrders && internalOrders.length > 0)) ? (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-red-50/50 p-4 border-b border-red-100 flex items-center justify-between">
                      <h3 className="font-semibold text-red-900 flex items-center">
                        <Icons.Package className="w-4 h-4 mr-2" />
                        Resumen de Productos
                      </h3>
                      <div className="flex space-x-2">
                        {order.items?.length > 0 && (
                          <span className="text-xs font-medium bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
                            {order.items.length} Siigo
                          </span>
                        )}
                        {internalOrders?.length > 0 && (
                          <span className="text-xs font-medium bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full">
                            {internalOrders.reduce((sum, io) => sum + (io.items?.length || 0), 0)} Especiales
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Tabla compacta de items */}
                    <div className="overflow-x-auto">
                      <div className="max-h-80 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                            <tr>
                              <th className="text-left p-3 font-semibold text-gray-700">Producto</th>
                              <th className="text-center p-3 font-semibold text-gray-700 w-20">Cant.</th>
                              <th className="text-right p-3 font-semibold text-gray-700 w-28">Precio</th>
                              <th className="text-right p-3 font-semibold text-gray-700 w-28">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Items de Siigo */}
                            {order.items?.map((item, index) => (
                              <tr key={`siigo-${index}`} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                                <td className="p-2">
                                  <div className="font-medium text-gray-900 text-xs leading-tight">
                                    {item.name || 'Producto sin nombre'}
                                  </div>
                                  {item.product_code && (
                                    <div className="text-gray-500 text-[10px] mt-0.5">
                                      Cód: {item.product_code}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 text-center font-medium">
                                  {item.quantity || 1}
                                </td>
                                <td className="p-2 text-right font-medium">
                                  ${(item.price || 0).toLocaleString('es-CO')}
                                </td>
                                <td className="p-2 text-right font-semibold text-green-600">
                                  ${((item.quantity || 1) * (item.price || 0)).toLocaleString('es-CO')}
                                </td>
                              </tr>
                            ))}

                            {/* Divider if both exist */}
                            {order.items?.length > 0 && internalOrders?.length > 0 && (
                              <tr className="bg-gray-100">
                                <td colSpan="4" className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase">Productos Especiales (POS Interno)</td>
                              </tr>
                            )}

                            {/* Items de Pedidos Internos */}
                            {internalOrders?.map((io) => (
                              io.items?.map((item, index) => (
                                <tr key={`internal-${io.id}-${index}`} className="border-b border-gray-100 hover:bg-orange-50 bg-orange-50/20">
                                  <td className="p-2">
                                    <div className="font-medium text-gray-900 text-xs leading-tight">
                                      {item.name || item.product_name || 'Producto especial'}
                                    </div>
                                    <div className="text-orange-600 text-[10px] mt-0.5 flex items-center">
                                      <Icons.Link className="w-2 h-2 mr-1" />
                                      {io.order_number}
                                    </div>
                                  </td>
                                  <td className="p-2 text-center font-medium">
                                    {item.quantity || 1}
                                  </td>
                                  <td className="p-2 text-right font-medium text-gray-600">
                                    ${(item.price || 0).toLocaleString('es-CO')}
                                  </td>
                                  <td className="p-2 text-right font-semibold text-orange-600">
                                    ${(parseFloat(item.subtotal) || (item.quantity * item.price) || 0).toLocaleString('es-CO')}
                                  </td>
                                </tr>
                              ))
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Total consolidado */}
                      <div className="bg-gray-100 border-t border-gray-200 p-4">
                        <div className="space-y-1">
                          <div className="flex justify-between items-center text-xs text-gray-600">
                            <span>Subtotal Siigo ({order.order_number}):</span>
                            <span>${(order.total_amount || 0).toLocaleString('es-CO')}</span>
                          </div>
                          {internalOrders?.length > 0 && (
                            <div className="flex justify-between items-center text-orange-600 font-medium">
                              <span className="text-xs">Pedidos Especiales ({internalOrders.length}):</span>
                              <span className="text-lg font-bold">+ ${(internalOrders.reduce((sum, io) => sum + parseFloat(io.total_amount || 0), 0)).toLocaleString('es-CO')}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center text-base pt-2 mt-2 border-t border-gray-300">
                            <span className="font-bold text-gray-900 uppercase tracking-tight">Total Consolidado:</span>
                            <span className="font-black text-green-600 text-xl">
                              ${(
                                parseFloat(order.total_amount || 0) +
                                internalOrders.reduce((sum, io) => sum + parseFloat(io.total_amount || 0), 0)
                              ).toLocaleString('es-CO')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-8 text-center shadow-sm">
                    <Icons.Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay productos en este pedido</p>
                  </div>
                )}

              </div>

              {/* Column 2: Configuration & Actions (Right) */}
              <div className="space-y-2">
                {/* Mensaje si no hay items */}
                {(!order.items || order.items.length === 0) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-1.5 flex items-start space-x-3 shadow-sm">
                    <Icons.AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-800">Pedido sin productos</h4>
                      <p className="text-xs text-yellow-700">Este registro parece no tener productos vinculados.</p>
                    </div>
                  </div>
                )}

                {/* Sección de Configuración Principal */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm space-y-2">
                  <div className="flex items-center space-x-2 pb-2 border-b border-gray-100">
                    <Icons.Settings className="w-4 h-4 text-purple-500" />
                    <h3 className="font-semibold text-gray-900">Configuración del Pedido</h3>
                  </div>

                  {/* Toggle Servicio */}
                  <div className="bg-purple-50/50 border border-purple-100 rounded-lg p-1.5 transition-all hover:bg-purple-50">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          checked={formData.is_service}
                          onChange={(e) => handleInputChange('is_service', e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 transition-colors"
                        />
                      </div>
                      <div>
                        <span className="block font-semibold text-purple-900">Pedido de Servicio</span>
                        <p className="text-xs text-purple-700 leading-relaxed">
                          Omite el flujo de despacho físico. Útil para fletes, ajustes o servicios puros.
                        </p>
                      </div>
                    </label>
                  </div>
                  {/* Datos de Despacho (Solo si NO es servicio) */}
                  {!formData.is_service && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase flex items-center">
                          <Icons.Truck className="w-3.5 h-3.5 mr-1" />
                          Método de Envío
                        </label>
                        <CustomDropdown
                          value={formData.delivery_method}
                          onChange={(value) => handleInputChange('delivery_method', value)}
                          options={deliveryMethods}
                          placeholder="Seleccionar..."
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase flex items-center">
                          <Icons.Calendar className="w-3.5 h-3.5 mr-1" />
                          Fecha de Despacho
                        </label>
                        <input
                          type="date"
                          value={formData.shipping_date}
                          onChange={(e) => handleInputChange('shipping_date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all text-xs outline-none"
                        />
                      </div>
                    </div>
                  )}

                  {/* Recibo de Gestión en Piso - Obligatorio para Bodega */}
                  {formData.delivery_method === 'recoge_bodega' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center space-x-2 text-amber-800 font-bold text-xs mb-2 uppercase">
                        <Icons.FileText className="w-4 h-4" />
                        <span>Recibo de Gestión en Piso</span>
                      </div>
                      <input
                        type="text"
                        value={formData.piso_receipt_number}
                        onChange={(e) => handleInputChange('piso_receipt_number', e.target.value)}
                        placeholder="Consecutivo recibo físico..."
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium"
                      />
                      <p className="text-[10px] text-amber-600 mt-1.5 italic">
                        * Este campo es obligatorio para el cierre de facturación
                      </p>
                    </div>
                  )}

                  {/* Método de Pago */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase flex items-center">
                      <Icons.CreditCard className="w-3.5 h-3.5 mr-1" />
                      Método de Pago
                    </label>
                    <CustomDropdown
                      value={formData.payment_method}
                      onChange={(value) => handleInputChange('payment_method', value)}
                      options={paymentMethods}
                      placeholder="Seleccionar medio de pago..."
                    />
                  </div>

                  {/* Carga de Comprobantes */}
                  {normalizePaymentMethod(formData.payment_method) === 'transferencia' && (
                    <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 animate-in fade-in slide-in-from-top-2 duration-300">
                      <h4 className="font-semibold text-blue-900 mb-4 flex items-center">
                        <Icons.Upload className="w-4 h-4 mr-2" />
                        Comprobantes de Pago
                      </h4>
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
                        <MultiplePaymentEvidenceUpload
                          orderId={order.id}
                          onEvidencesChange={(count) => setEvidenceCount(count)}
                        />
                      </div>
                      <p className="text-[11px] text-blue-600 mt-3 flex items-center">
                        <Icons.Info className="w-3 h-3 mr-1" />
                        Se recomienda subir el soporte para agilizar la validación
                      </p>
                    </div>
                  )}

                  {/* Opciones de Pago Electrónico */}
                  {formData.payment_method === 'pago_electronico' && (
                    <div className="bg-cyan-50/50 border border-cyan-100 rounded-lg p-5 animate-in fade-in slide-in-from-top-2 duration-300">
                      <h4 className="font-semibold text-cyan-900 mb-4 flex items-center">
                        <Icons.CreditCard className="w-4 h-4 mr-2" />
                        Detalles de Pago Electrónico
                      </h4>
                      <div className="space-y-1.5">
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-500 uppercase">Medio Electrónico</label>
                          <select
                            value={formData.electronic_payment_type}
                            onChange={(e) => handleInputChange('electronic_payment_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none transition-all text-xs"
                          >
                            <option value="">Seleccionar plataforma...</option>
                            <option value="mercadopago">MercadoPago</option>
                            <option value="bold">Bold</option>
                            <option value="otro">Otro medio</option>
                          </select>
                        </div>

                        {formData.electronic_payment_type === 'otro' && (
                          <div className="space-y-2 animate-in zoom-in-95 duration-200">
                            <label className="text-xs font-bold text-gray-500 uppercase">Especificar medio</label>
                            <input
                              type="text"
                              value={formData.electronic_payment_notes}
                              onChange={(e) => handleInputChange('electronic_payment_notes', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-xs"
                              placeholder="Ej: Nequi, PayU..."
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recomendación del sistema */}
                  {recommendation && (
                    <div className={`p-4 rounded-lg border-l-4 shadow-sm transition-all ${recommendation.action === 'wallet'
                      ? 'bg-blue-50/50 border-blue-500'
                      : 'bg-green-50/50 border-green-500'
                      }`}>
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${recommendation.action === 'wallet' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                          <Icons.ShieldCheck className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Ruta Sugerida
                          </h4>
                          <p className="text-xs text-gray-800 font-medium">
                            {recommendation.reason}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Notas de la Factura SIIGO */}
              {(order.siigo_observations || order.notes) && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-blue-50/50 p-4 border-b border-blue-100 flex items-center">
                    <Icons.StickyNote className="w-4 h-4 text-blue-600 mr-2" />
                    <h3 className="font-semibold text-blue-900">Observaciones de Facturación</h3>
                  </div>
                  <div className="p-4">
                    <div className="bg-blue-50/30 rounded-lg p-1.5 border border-blue-100">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                        {(() => {
                          // Priorizar observaciones SIIGO si existen, de lo contrario usar notes
                          let textToFormat = order.siigo_observations || order.notes || '';

                          // Lista de campos a identificar y separar (mejora legibilidad)
                          const fieldsToSeparate = [
                            'ESTADO DE PAGO:',
                            'MEDIO DE PAGO:',
                            'FORMA DE PAGO DE ENVIO:',
                            'NOMBRE:',
                            'NIT:',
                            'TELÉFONO:',
                            'DEPARTAMENTO:',
                            'CIUDAD:',
                            'DIRECCIÓN:',
                            'NOTA:'
                          ];

                          fieldsToSeparate.forEach(field => {
                            const pattern = new RegExp(`([^\\n])${field.replace(/[.*+?^${}()|[\]\\\\]/g, '\\$&')}`, 'g');
                            textToFormat = textToFormat.replace(pattern, `$1\n${field}`);
                          });

                          textToFormat = textToFormat
                            .replace(/\r\n/g, '\n')
                            .replace(/\r/g, '\n')
                            .replace(/\n+/g, '\n')
                            .split('\n')
                            .map(line => line.replace(/\s+/g, ' ').trim())
                            .filter(line => line.length > 0)
                            .join('\n');

                          // Si hay ambos campos y son duplicados, mostrar solo uno (ya priorizamos observaciones)
                          return textToFormat;
                        })()}
                      </pre>
                    </div>
                  </div>
                  <p className="text-[10px] text-blue-500 mt-2 italic">
                    * Estas notas fueron importadas directamente desde el sistema contable SIIGO
                  </p>
                </div>
              )}

              {/* Timeline de Notas Colaborativas - DESHABILITADO (backend inestable) */}
              {/* <div className="mt-3">
                <OrderNotesTimeline orderId={order.id} user={user} />
              </div> */}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          {/* Botones principales */}
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>

            {/* Botones dinámicos según el rol del usuario */}
            {['admin', 'facturador'].includes(user?.role) ? (
              // Para admin y facturador: un solo botón que aplica reglas de negocio automáticas
              <button
                onClick={handleProcessOrder}
                disabled={loading || inventoryCheck.checking || mixturesCheck.checking || !inventoryCheck.valid || !mixturesCheck.valid || !formData.payment_method || (!formData.is_service && (!formData.shipping_date || !formData.delivery_method))}
                className={`flex items-center px-4 py-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${(!inventoryCheck.valid || !mixturesCheck.valid)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                title={!inventoryCheck.valid ? 'No hay inventario suficiente para procesar este pedido' : !mixturesCheck.valid ? 'Desbalance de mezclas A/B' : 'Procesar Pedido'}
              >
                {loading ? (
                  <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (inventoryCheck.checking || mixturesCheck.checking) ? (
                  <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : !inventoryCheck.valid ? (
                  <Icons.Lock className="w-4 h-4 mr-2" />
                ) : !mixturesCheck.valid ? (
                  <Icons.AlertTriangle className="w-4 h-4 mr-2" />
                ) : (
                  <Icons.Zap className="w-4 h-4 mr-2" />
                )}
                {mixturesCheck.checking ? 'Validando mezclas...' : inventoryCheck.checking ? 'Validando stock...' : !inventoryCheck.valid ? '🔒 Inventario Insuficiente' : !mixturesCheck.valid ? '🚫 Mezclas Desbalanceadas' : 'Procesar Pedido'}
              </button>
            ) : (
              // Para otros roles: botones manuales
              <div className="flex space-x-3">
                <button
                  onClick={handleSendToWallet}
                  disabled={loading || inventoryCheck.checking || !inventoryCheck.valid || !formData.payment_method || !formData.shipping_date}
                  className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  title={!inventoryCheck.valid ? 'No hay inventario suficiente para procesar este pedido' : 'Enviar a Cartera'}
                >
                  {loading || inventoryCheck.checking ? (
                    <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : !inventoryCheck.valid ? (
                    <Icons.Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <Icons.CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {inventoryCheck.checking ? 'Validando...' : !inventoryCheck.valid ? 'Stock Insuficiente' : 'Enviar a Cartera'}
                </button>

                <button
                  onClick={handleSendToLogistics}
                  disabled={loading || inventoryCheck.checking || !inventoryCheck.valid || !formData.payment_method || !formData.shipping_date || !formData.delivery_method}
                  className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  title={!inventoryCheck.valid ? 'No hay inventario suficiente para procesar este pedido' : 'Enviar a Logística'}
                >
                  {loading || inventoryCheck.checking ? (
                    <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : !inventoryCheck.valid ? (
                    <Icons.Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <Icons.Package className="w-4 h-4 mr-2" />
                  )}
                  {inventoryCheck.checking ? 'Validando...' : !inventoryCheck.valid ? 'Stock Insuficiente' : 'Enviar a Logística'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay Centrado de Validación de Inventario */}
      {(inventoryCheck.checking || inventoryCheck.issues.length > 0) && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[10001] rounded-lg">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-6 transform transition-all">
            {inventoryCheck.checking ? (
              <div className="text-center py-8">
                <Icons.Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Validando Inventario...</h3>
                <p className="text-gray-600">Verificando disponibilidad de productos</p>
              </div>
            ) : inventoryCheck.issues.length > 0 ? (
              <div>
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-red-100 rounded-full p-3">
                    <Icons.AlertTriangle className="w-12 h-12 text-red-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-center text-red-900 mb-4">
                  ⚠️ INVENTARIO INSUFICIENTE
                </h3>
                <div className="bg-red-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                  <div className="space-y-2">
                    {inventoryCheck.issues.map((issue, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-red-200">
                        <div className="font-semibold text-gray-900 mb-1">{issue.product_name}</div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Solicitado:</span>
                            <div className="font-bold text-gray-900">{issue.quantity_requested}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Disponible:</span>
                            <div className="font-bold text-red-600">{issue.stock_available}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Faltante:</span>
                            <div className="font-bold text-red-600">{issue.shortage}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <Icons.Info className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900">
                      <strong>Acción requerida:</strong> Actualiza la factura en Siigo, elimina o reduce los productos sin stock, sincroniza desde Siigo y vuelve a abrir este modal para revalidar.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setInventoryCheck(prev => ({ ...prev, issues: [] }))}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                >
                  Entendido
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Modal de Validación de Mezclas */}
      {showMixturesModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-3xl">⚠️</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Desbalance de Mezclas</h2>
                    <p className="text-red-100 text-sm mt-1">
                      No se puede procesar este pedido
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMixturesModal(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <span className="text-2xl leading-none">×</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
              <div className="mb-4">
                <p className="text-gray-700 text-base leading-relaxed">
                  Se ha detectado un <strong>desbalance en las mezclas A/B</strong> de este pedido.
                  Los siguientes productos no tienen el ratio correcto (debe ser 1:1):
                </p>
              </div>

              {/* Lista de productos afectados */}
              <div className="space-y-3">
                {mixturesCheck.affected_items.map((item, index) => (
                  <div key={index} className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg mb-1">
                          {item.product_code || item.internal_code}
                        </h3>
                        <p className="text-gray-700 font-medium mb-2">
                          {item.product_name}
                        </p>

                        {/* Detalles del desbalance */}
                        <div className="bg-white rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Cantidad en pedido (Mezcla A):</span>
                            <span className="font-bold text-gray-900">{item.quantity_in_order || 0} unidades</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Leche requerida (Mezcla B):</span>
                            <span className="font-bold text-gray-900">{item.required_milk || 0} unidades</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Leche en pedido:</span>
                            <span className="font-bold text-red-600">{item.milk_in_order || 0} unidades</span>
                          </div>
                          {item.associated_milk_code && (
                            <div className="pt-2 border-t border-gray-200">
                              <span className="text-xs text-gray-500">
                                Leche asociada: <strong>{item.associated_milk_code}</strong>
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Mensaje de alerta */}
                        {item.alert_message && (
                          <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-800 font-medium">
                              {item.alert_message}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Instrucciones */}
              <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💡</span>
                  <div>
                    <h4 className="font-bold text-blue-900 mb-2">¿Qué hacer?</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Corrige el pedido en Siigo agregando las mezclas faltantes</li>
                      <li>• Asegúrate de que cada sabor tenga su leche correspondiente</li>
                      <li>• La relación debe ser 1:1 (1 sabor = 1 leche)</li>
                      <li>• Luego recarga este modal para validar nuevamente</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowMixturesModal(false)}
                className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold transition-colors"
              >
                Entendido
              </button>
              <button
                onClick={() => {
                  setShowMixturesModal(false);
                  validateMixtures(); // Revalidar
                }}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2"
              >
                <span>🔄</span>
                <span>Validar Nuevamente</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default OrderReviewModal;
